import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { aiService } from '../../services/aiService';

const CON_CATS = ['recipe','lifestyle','quote','education','promo'];

const URGENCY_STYLE = {
  hot:     { bg:'var(--rose-l)',  color:'var(--rose)',  label:'🔥 Hot' },
  soon:    { bg:'var(--gold-l)',  color:'var(--gold)',  label:'📌 Soon' },
  anytime: { bg:'var(--fog)',     color:'#9ca3af',      label:'Anytime' },
};

export default function ConsumerIdeas({ onNavigateCreate }) {
  const { ideas, conPosts, addIdea, updateIdea, anthropicKey, anthropicKeyReady, voiceState, logActivity } = useApp();
  const [suggesting, setSuggesting] = useState(false);

  const conIdeas = ideas.filter(i =>
    i.status !== 'archived' &&
    (i.world === 'consumer' || CON_CATS.includes(i.category))
  );
  const hotCount       = conIdeas.filter(i => i.urgency === 'hot').length;
  const draftCount     = conPosts.filter(p => p.status === 'draft').length;
  const scheduledCount = conPosts.filter(p => p.scheduled_date && p.status !== 'published').length;
  const publishedCount = conPosts.filter(p => p.status === 'published').length;

  async function runAISuggestions() {
    if (!anthropicKeyReady) { alert('Add your Anthropic API key in Settings → API Keys first.'); return; }
    setSuggesting(true);
    try {
      const voiceBlock = voiceState?.description ? `Brand voice: ${voiceState.description}` : '';
      const prompt = `Generate 6 consumer content ideas for Tayst Coffee.
Tayst = premium sustainable Rainforest Alliance certified coffee, compostable pods.
Today: ${new Date().toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' })}.
${voiceBlock}
Return JSON array only:
[{"title":"specific idea","category":"recipe|lifestyle|quote|education|promo","urgency":"hot|soon|anytime","notes":"2-3 sentences","why":"why now"}]`;

      const text = await aiService.rawComplete({ message: prompt, maxTokens: 1024, anthropicKey });
      const match = text.replace(/```json|```/g,'').trim().match(/\[[\s\S]*\]/);
      if (match) {
        const suggestions = JSON.parse(match[0]);
        await Promise.all(suggestions.map(s => addIdea({
          source:'ai', title:s.title, notes:s.notes,
          category:s.category||'lifestyle', urgency:s.urgency||'soon',
          status:'new', world:'consumer', ai_why:s.why,
        })));
        logActivity(`AI generated ${suggestions.length} consumer ideas`);
      }
    } catch (e) {
      alert('AI suggestion failed: ' + (e.message || String(e)));
    }
    setSuggesting(false);
  }

  const sorted = [...conIdeas].sort((a, b) => {
    const u = { hot:0, soon:1, anytime:2 };
    return (u[a.urgency]??2) - (u[b.urgency]??2) || new Date(b.created_at||0) - new Date(a.created_at||0);
  });

  return (
    <div style={{ display:'flex', height:'100%', minHeight:0 }}>
      <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700 }}>Consumer Ideas</div>
            <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>
              Recipes · Lifestyle · Quotes · Promotions
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-outline btn-sm" onClick={runAISuggestions} disabled={suggesting}>
              {suggesting ? <span className="dots"><i/><i/><i/></span> : '🤖 AI Suggest'}
            </button>
            <button className="btn btn-primary btn-sm"
              style={{ background:'var(--rose)', borderColor:'var(--rose)' }}
              onClick={() => onNavigateCreate?.()}>
              + Add Idea
            </button>
          </div>
        </div>

        {hotCount > 0 && (
          <div className="info-box rose" style={{ marginBottom:12 }}>
            <span style={{ fontSize:15, flexShrink:0 }}>🔥</span>
            <div>
              <strong>{hotCount} urgent idea{hotCount>1?'s':''}</strong>:{' '}
              {conIdeas.filter(i=>i.urgency==='hot').map(i=>i.title.slice(0,40)).join(' · ')}
            </div>
          </div>
        )}

        {sorted.length === 0 ? (
          <div className="empty-state" style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1.5px dashed var(--border)', padding:36 }}>
            <div style={{ fontSize:28, marginBottom:8 }}>💡</div>
            No consumer ideas yet. Click <strong>AI Suggest</strong> or add one.
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:10, marginBottom:20 }}>
            {sorted.map(idea => {
              const urgStyle = URGENCY_STYLE[idea.urgency] || URGENCY_STYLE.anytime;
              return (
                <div key={idea.id} style={{ background:'var(--white)', borderRadius:'var(--r)',
                  border:`1.5px solid ${idea.urgency==='hot'?'var(--rose)':idea.urgency==='soon'?'var(--gold-l)':'var(--border)'}`,
                  overflow:'hidden', boxShadow:'var(--sh)' }}>
                  <div style={{ padding:'9px 12px 7px', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6 }}>
                    <div style={{ fontSize:12.5, fontWeight:700, flex:1, lineHeight:1.35 }}>{idea.title}</div>
                    <span style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', padding:'2px 6px', borderRadius:5,
                      whiteSpace:'nowrap', background:urgStyle.bg, color:urgStyle.color }}>{urgStyle.label}</span>
                  </div>
                  {idea.ai_why && <div style={{ padding:'0 12px 6px', fontSize:10, color:'var(--caramel)', fontStyle:'italic' }}>💡 {idea.ai_why}</div>}
                  {idea.notes && <div style={{ padding:'0 12px 8px', fontSize:11, color:'#6b7280', lineHeight:1.5 }}>{idea.notes.slice(0,100)}{idea.notes.length>100?'…':''}</div>}
                  <div style={{ padding:'7px 12px', borderTop:'1px solid var(--border)', display:'flex', gap:5, background:'var(--fog)' }}>
                    <button className="btn btn-xs" style={{ background:'var(--rose)', color:'#fff', border:'none' }} onClick={() => onNavigateCreate?.(idea)}>
                      → Create Post
                    </button>
                    <button className="btn btn-ghost btn-xs" onClick={() => updateIdea(idea.id, { status:'archived' }).catch(console.error)}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div style={{ width:220, borderLeft:'1px solid var(--border)', overflowY:'auto', flexShrink:0 }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', background:'var(--fog)' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:14, fontWeight:700, marginBottom:10 }}>Consumer Stats</div>
          {[['Drafts',draftCount],['Scheduled',scheduledCount],['Published',publishedCount]].map(([label,num]) => (
            <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
              <span style={{ color:'#6b7280' }}>{label}</span>
              <span style={{ fontWeight:700 }}>{num}</span>
            </div>
          ))}
        </div>
        <div style={{ padding:'14px 16px' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:14, fontWeight:700, marginBottom:10 }}>Quick Actions</div>
          <button className="btn btn-xs" style={{ width:'100%', justifyContent:'center', marginBottom:6, background:'var(--rose)', color:'#fff', border:'none' }} onClick={() => onNavigateCreate?.()}>✨ Create New Post</button>
          <button className="btn btn-outline" style={{ width:'100%', justifyContent:'center', fontSize:10.5 }} onClick={runAISuggestions} disabled={suggesting}>🤖 AI Suggest Ideas</button>
        </div>
      </div>
    </div>
  );
}
